import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FinePrint } from "@/components/FinePrint";
import { Loader2, ClipboardList } from "lucide-react";

const TABS = [
  { v: "open", label: "Open" },
  { v: "my", label: "My Tasks" },
  { v: "unassigned", label: "Unassigned" },
  { v: "registration", label: "Registration Requests" },
  { v: "support", label: "Support Requests" },
  { v: "completed", label: "Completed" },
];
const TASK_STATUS = {
  open: "bg-amber-100 text-amber-800", in_review: "bg-sky-100 text-sky-800",
  waiting_applicant: "bg-purple-100 text-purple-800", completed: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL = { open: "Open", in_review: "In Review", waiting_applicant: "Waiting for Applicant", completed: "Completed", closed: "Closed" };

export function AdminTasks() {
  const [tab, setTab] = useState("open");
  const [rows, setRows] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/tasks", { params: { filter: tab } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(null); load(); }, [tab]);
  useEffect(() => { api.get("/admin/registration/admins").then(({ data }) => setAdmins(data)).catch(() => {}); }, []);

  const setStatus = async (t, status) => {
    setBusy(t.id);
    try { await api.post(`/admin/tasks/${t.id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };
  const setAssignee = async (t, assignee_id) => {
    setBusy(t.id);
    try { await api.post(`/admin/tasks/${t.id}/assign`, { assignee_id: assignee_id === "none" ? null : assignee_id }); toast.success("Assignment updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <DashboardShell title="Administrative Tasks" subtitle="Every registration & support work item in one queue" nav={ADMIN_NAV}>
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tasks-tab-${t.v}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === t.v ? "bg-[#003057] text-white" : "bg-white text-slate-500 hover:bg-slate-100 border border-slate-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {!rows && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {rows && (
        <Card className="p-2 card-elevate sm:p-4">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Type</TableHead><TableHead>Title</TableHead><TableHead>Applicant</TableHead>
              <TableHead>Created</TableHead><TableHead>Assigned</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Update</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} data-testid={`task-row-${t.id}`}>
                  <TableCell><span className="inline-flex items-center gap-1 text-xs font-semibold text-[#003057]"><ClipboardList className="h-3.5 w-3.5" />{t.type === "new_user_registration" ? "Registration" : "Support"}</span></TableCell>
                  <TableCell className="text-sm font-medium text-[#003057]">{t.title}</TableCell>
                  <TableCell className="text-xs text-slate-500">{t.applicant_email || "—"}</TableCell>
                  <TableCell className="font-mono-num text-xs text-slate-400">{t.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <Select value={t.assigned_to || "none"} onValueChange={(v) => setAssignee(t, v)} disabled={busy === t.id}>
                      <SelectTrigger className="w-40" data-testid={`task-assign-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${TASK_STATUS[t.status] || TASK_STATUS.closed}`}>{STATUS_LABEL[t.status] || t.status}</span></TableCell>
                  <TableCell className="text-right">
                    <Select value={t.status} onValueChange={(v) => setStatus(t, v)} disabled={busy === t.id}>
                      <SelectTrigger className="w-40" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(STATUS_LABEL).map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400" data-testid="tasks-empty">No tasks in this view.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}
      <FinePrint>
        Tasks are auto-created for every registration request and stay synchronized with the request's status,
        so nothing is lost even if a notification email fails to deliver. This shared queue is reusable for future
        GPA+ administrative workflows.
      </FinePrint>
    </DashboardShell>
  );
}
